import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { ApiError } from '../../../lib/api';
import { chatApi } from '../../../lib/chat';
import { useSessionStream } from '../../../hooks/useSessionStream';
import { ConsultantMessage } from './ConsultantMessage';
import { ConsultantChatHeader } from './ConsultantChatHeader';
import { ConsultantChatInput } from './ConsultantChatInput';
import { toast } from 'sonner';
import type { ChatMessage, Consultant } from '../../../types';

interface ConsultantChatProps {
  consultant: Consultant;
  onClose: () => void;
}

export const ConsultantChat: React.FC<ConsultantChatProps> = ({ consultant, onClose }) => {
  const { user, setCredits } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const { messages, connected, mergeMessages } = useSessionStream(sessionId);

  // Sessão de consultor nasce quando o user abre o chat — reaproveita a última com este consultor
  useEffect(() => {
    if (!user) return;

    let cancelled = false;

    const ensureSession = async () => {
      setSessionId(null);
      try {
        const { sessions } = await chatApi.listSessions();
        const mine = sessions.filter(
          s => s.sessionType === 'consultant' && s.consultantId === consultant.id
        );
        const last = mine.sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];

        if (last) {
          if (!cancelled) setSessionId(last.id);
          return;
        }

        const { session } = await chatApi.createSession({
          sessionType: 'consultant',
          consultantId: consultant.id
        });
        if (!cancelled) setSessionId(session.id);
      } catch (err) {
        if (!cancelled) {
          toast.error('Erro ao abrir o chat com o consultor');
          onClose();
        }
        console.error('ConsultantChat: erro ao abrir sessão', err);
      }
    };

    ensureSession();
    return () => { cancelled = true; };
  }, [user, consultant.id, onClose]);

  // Backfill do histórico da sessão
  useEffect(() => {
    if (!sessionId) return;

    let cancelled = false;

    const loadHistory = async () => {
      try {
        const { messages: history } = await chatApi.getMessages(sessionId);
        if (!cancelled) mergeMessages(history);
      } catch (err) {
        if (!cancelled) toast.error('Erro ao carregar o histórico do chat');
        console.error('ConsultantChat: erro no backfill', err);
      }
    };

    loadHistory();
    return () => { cancelled = true; };
  }, [sessionId, mergeMessages]);

  // Autoscroll
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async (content: string) => {
    if (!sessionId || !user || isSending) return;

    setIsSending(true);
    try {
      const response = await chatApi.sendMessage(sessionId, content);
      if (typeof response.credits === 'number') {
        setCredits(response.credits);
      }
      mergeMessages([response.userMsg]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        toast.error('Créditos insuficientes para continuar o chat');
      } else {
        toast.error(err instanceof Error ? err.message : 'Erro ao enviar mensagem');
      }
    } finally {
      setIsSending(false);
    }
  };

  const displayMessages: ChatMessage[] = messages.map(m => ({
    id: m.id,
    content: m.content,
    sender: m.senderRole,
    timestamp: new Date(m.createdAt)
  }));

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-indigo-950 rounded-lg w-full max-w-2xl h-[80vh] flex flex-col">
        <ConsultantChatHeader consultant={consultant} onClose={onClose} />
        <div className="px-4 pb-2 text-center text-xs text-purple-200/60">
          {connected ? 'conectado' : 'reconectando…'}
        </div>

        <div
          ref={chatRef}
          className="flex-1 overflow-y-auto p-4 space-y-4"
        >
          {displayMessages.map((message) => (
            <ConsultantMessage
              key={message.id}
              message={message}
              consultant={consultant}
            />
          ))}
        </div>

        <ConsultantChatInput
          onSendMessage={handleSendMessage}
          isLoading={isSending}
          pricePerMinute={consultant.chatPrice}
        />
      </div>
    </div>
  );
};